import { isEqual } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { AlertsConfig, ChangesAlerts } from '../../../types'
import { pubSubAlerts } from '../../state/pubSub'
import {
    clearAlerts as clearAlertState,
    resetUnread as resetUnreadState,
    state,
} from '../../state/state.alerts'

const unreadInitialState = (skipUnread: boolean) => {
    if (skipUnread) return 0
    return state.unread
}

const alertsInitialState = (id: number, updatesOnly?: boolean) => {
    const alerts = Array.from(state.alerts.values())
    if (updatesOnly) return []
    if (!id) return alerts
    return alerts.filter(({ id: target }) => id === target)
}

interface AlertsData {
    alerts: ChangesAlerts[]
    unread: number
}

interface UseAlerts {
    config?: AlertsConfig
    updatesOnly?: boolean
    unreadCount?: boolean
    unreadID?: number
    id?: number
}

export function useAlerts({ updatesOnly, unreadCount, config, id }: UseAlerts) {
    const skipUnread = !unreadCount || updatesOnly || !!id
    const [stateConfig, setConfig] = useState<AlertsConfig>(config)
    const [alerts, setAlerts] = useState(() =>
        alertsInitialState(id, updatesOnly),
    )
    const [unread, setUnread] = useState(() => unreadInitialState(skipUnread))

    useEffect(() => {
        if (!isEqual(stateConfig, config)) {
            setConfig(config)
        }
    }, [config])

    const resetUnread = useCallback(() => {
        if (skipUnread) return
        setUnread(0)
        resetUnreadState()
    }, [skipUnread])

    const clearAlerts = useCallback(() => {
        setUnread(0)
        clearAlertState()
    }, [])

    useEffect(() => {
        const callback = (data: AlertsData) => {
            const { alerts: incomingAlerts, unread } = data
            if (unreadCount && !updatesOnly && !id) {
                setUnread(unread)
            }
            if (updatesOnly) {
                setAlerts([...incomingAlerts])
                return
            }
            if (id) {
                setAlerts([...alerts, ...incomingAlerts])
                return
            }
            setAlerts(Array.from(state.alerts.values()))
        }

        if (id) {
            return pubSubAlerts.on(`ALERT::${id}`, callback, stateConfig)
        }

        return pubSubAlerts.on('ALERT', callback, stateConfig)
    }, [stateConfig, unreadCount, updatesOnly, id])

    if (skipUnread) {
        return { alerts }
    }

    return { alerts, unread, clearAlerts, resetUnread }
}
